/**
 * AlertsManager — dialog with two distinct modes.
 *
 *   mode="manage"  (BurgerMenu → "Spread Alerts")
 *     - Pairing status / connect Telegram
 *     - Read-only list of existing subscriptions (with delete)
 *     - NO add form — adding subscriptions only happens via the cell-dialog
 *       🔔 button. This keeps the burger menu focused on telemetry +
 *       pairing, and ensures every new subscription comes from a chart
 *       context where the user is looking at actual spread numbers.
 *
 *   mode="add"  (AllChartsPanel cell dialog → 🔔)
 *     - Compact add-subscription form prefilled with the clicked pair and
 *       suggested levels (mean / p5 / p95).
 *     - Pairing banner only if NOT paired (otherwise hidden — paired user
 *       just sees the form).
 *     - NO list of other subscriptions, NO pair pickers — the pair is
 *       fixed by the calling chart.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, Chip,
  Alert as MuiAlert, CircularProgress, TextField,
  List, ListItem, ListItemText, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TelegramIcon from '@mui/icons-material/Telegram';

import {
  startPairing, fetchPairingStatus,
  fetchSubscriptions, upsertSubscription, deleteSubscription,
  clearPairingCode,
} from '../store/alertsSlice';

const POLL_PAIRING_MS = 2000;
const POLL_PAIRING_TIMEOUT_MS = 5 * 60 * 1000;

const AlertsManager = ({ open, onClose, mode = 'manage', prefill }) => {
  const dispatch = useDispatch();

  const paired = useSelector((s) => s.alerts.paired);
  const pairingCode = useSelector((s) => s.alerts.pairingCode);
  const subs = useSelector((s) => s.alerts.subscriptions);
  const error = useSelector((s) => s.alerts.error);
  const loading = useSelector((s) => s.alerts.loading);

  // 'add' mode form state — pair is fixed by `prefill`. Editable:
  //   - `levelsInput` : free-text comma/space-separated numbers
  //   - `cooldownInputs` : array of strings, one per parsed level
  // Stored as strings (not numbers) so the user can type "1, 2, 3" without
  // intermediate parses fighting their cursor; parsed at submit time.
  // 0 = fire on every crossing for that level.
  const [levelsInput, setLevelsInput] = useState('');
  const [cooldownInputs, setCooldownInputs] = useState([]);

  // Bounds — mirror server's COOLDOWN_MIN/MAX/DEFAULT.
  const COOLDOWN_DEFAULT = 1800;
  const COOLDOWN_MIN = 0;
  const COOLDOWN_MAX = 86400;   // 24 h

  // ---- effects ---------------------------------------------------------
  // On every dialog open: refresh status + subs. If we come back already
  // paired (the most common case), proactively clear any stale pairingCode
  // sitting in Redux from a previous session — that's what was making the
  // banner flicker between "show code" and "✓ paired".
  useEffect(() => {
    if (!open) return;
    dispatch(fetchPairingStatus()).then((a) => {
      if (a.payload?.paired) dispatch(clearPairingCode());
    });
    dispatch(fetchSubscriptions());
  }, [open, dispatch]);

  // 'add' mode: prefill the levels input. Priority:
  //   1. If a subscription already exists for this pair → use its CURRENT
  //      levels (so the dialog acts as an editor — replace semantics on
  //      submit means what's shown is what gets saved).
  //   2. Otherwise → use the prefill.levels suggestion the chart provided
  //      (mean / p5 / p95).
  //   3. Otherwise → empty.
  //
  // Pair lookup uses sorted ticker order, matching the backend's storage
  // convention.
  const sortedPair = useMemo(() => {
    if (!prefill?.ticker1 || !prefill?.ticker2) return null;
    return [prefill.ticker1, prefill.ticker2].sort();
  }, [prefill?.ticker1, prefill?.ticker2]);

  const existingSub = useMemo(() => {
    if (!sortedPair) return null;
    return subs.find((s) => s.ticker1 === sortedPair[0] && s.ticker2 === sortedPair[1]) || null;
  }, [subs, sortedPair]);

  useEffect(() => {
    if (!open || mode !== 'add' || !prefill) return;
    let source = null;
    if (existingSub && Array.isArray(existingSub.levels) && existingSub.levels.length > 0) {
      source = existingSub.levels;
    } else if (Array.isArray(prefill.levels) && prefill.levels.length > 0) {
      source = prefill.levels;
    }

    // Per-level cooldown seed. Priority:
    //   1. existingSub.cooldownSecondsPerLevel[i] (new schema)
    //   2. existingSub.cooldownSeconds (legacy scalar) — fan out to all levels
    //   3. existingSub.cooldownMinutes × 60 (older legacy)
    //   4. COOLDOWN_DEFAULT
    const sourceLen = source ? source.filter((x) => Number.isFinite(Number(x))).length : 0;
    const cdSeed = (i) => {
      if (existingSub) {
        const arr = existingSub.cooldownSecondsPerLevel;
        if (Array.isArray(arr) && Number.isFinite(Number(arr[i]))) {
          return String(Number(arr[i]));
        }
        if (existingSub.cooldownSeconds != null) {
          return String(Number(existingSub.cooldownSeconds));
        }
        if (existingSub.cooldownMinutes != null) {
          return String(Number(existingSub.cooldownMinutes) * 60);
        }
      }
      return String(COOLDOWN_DEFAULT);
    };
    setCooldownInputs(Array.from({ length: sourceLen }, (_, i) => cdSeed(i)));

    if (source) {
      setLevelsInput(source
        .map((x) => Number(x))
        .filter((x) => Number.isFinite(x))
        .map((x) => x.toFixed(2))
        .join(', ')
      );
    } else {
      setLevelsInput('');
    }
  }, [open, mode, prefill, existingSub]);

  // Pairing-code poll: only while we have a code and aren't yet paired.
  // Auto-clears when the user pairs OR after 5 min.
  useEffect(() => {
    if (!open || paired || !pairingCode) return undefined;
    const id = setInterval(() => {
      dispatch(fetchPairingStatus());
    }, POLL_PAIRING_MS);
    const timeout = setTimeout(() => clearInterval(id), POLL_PAIRING_TIMEOUT_MS);
    return () => { clearInterval(id); clearTimeout(timeout); };
  }, [open, paired, pairingCode, dispatch]);

  // ---- handlers --------------------------------------------------------
  const handleConnect = useCallback(() => {
    dispatch(startPairing());
  }, [dispatch]);

  const parsedLevels = useMemo(() => {
    return levelsInput
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number)
      .filter((n) => Number.isFinite(n));
  }, [levelsInput]);

  // Sanitize one cooldown input to seconds in [0, 86400]. Empty → default.
  const sanitizeCd = useCallback((raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return COOLDOWN_DEFAULT;
    const n = Math.round(Number(s));
    if (!Number.isFinite(n)) return COOLDOWN_DEFAULT;
    return Math.max(COOLDOWN_MIN, Math.min(COOLDOWN_MAX, n));
  }, []);

  // Keep cooldownInputs aligned 1:1 with parsedLevels: extend with the
  // default when the user adds a new level, trim when they remove one.
  // We intentionally don't re-order on level value changes — this is a
  // positional editor: index i in the levels textbox maps to row i below.
  useEffect(() => {
    setCooldownInputs((prev) => {
      const target = parsedLevels.length;
      if (prev.length === target) return prev;
      if (prev.length < target) {
        return [...prev, ...Array(target - prev.length).fill(String(COOLDOWN_DEFAULT))];
      }
      return prev.slice(0, target);
    });
  }, [parsedLevels.length]);

  const parsedCooldowns = useMemo(() => {
    return parsedLevels.map((_, i) => sanitizeCd(cooldownInputs[i]));
  }, [parsedLevels, cooldownInputs, sanitizeCd]);

  const t1 = prefill?.ticker1;
  const t2 = prefill?.ticker2;
  const canAdd = !!t1 && !!t2 && t1 !== t2 && parsedLevels.length > 0 && paired;

  const handleAdd = useCallback(async () => {
    if (!canAdd) return;
    const action = await dispatch(upsertSubscription({
      ticker1: t1, ticker2: t2,
      levels: parsedLevels,
      cooldownSecondsPerLevel: parsedCooldowns,
    }));
    if (action.meta.requestStatus === 'fulfilled') {
      onClose?.();   // close after successful add
    }
  }, [dispatch, t1, t2, parsedLevels, parsedCooldowns, canAdd, onClose]);

  // Humanize cooldown for display in helper text and the manage-mode list.
  const humanizeCd = useCallback((seconds) => {
    const n = Number(seconds);
    if (!Number.isFinite(n) || n <= 0) return 'no cooldown';
    if (n < 60) return `${n}s`;
    if (n < 3600) {
      const m = n / 60;
      return Number.isInteger(m) ? `${m}m` : `${m.toFixed(1)}m`;
    }
    const h = n / 3600;
    return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`;
  }, []);

  const handleDelete = useCallback((id) => {
    dispatch(deleteSubscription(id));
  }, [dispatch]);

  const handleCopyCode = useCallback(() => {
    if (pairingCode) {
      navigator.clipboard?.writeText(`/start ${pairingCode}`).catch(() => {});
    }
  }, [pairingCode]);

  // ---- render: pairing banner (shared by both modes when relevant) -----
  const renderPairingBanner = () => {
    if (paired) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main', mb: 2 }}>
          <CheckCircleIcon />
          <Typography variant="body2">Telegram is paired. Alerts will be DM'd to your chat.</Typography>
        </Box>
      );
    }
    if (pairingCode) {
      return (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'background.default', border: 1, borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Connect Telegram (step 2 of 2)</Typography>
          <Typography variant="caption" color="text.secondary">
            Open the bot in Telegram and send this command. We'll detect the bind automatically.
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 700,
              px: 1.5, py: 0.5, borderRadius: 1, bgcolor: 'action.hover', flex: 1
            }}>
              /start {pairingCode}
            </Box>
            <IconButton size="small" onClick={handleCopyCode}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <CircularProgress size={16} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Code expires in 5 minutes.
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 2,
        p: 1.5, borderRadius: 1, bgcolor: 'background.default',
        border: 1, borderColor: 'divider'
      }}>
        <Typography variant="body2" sx={{ flex: 1 }}>
          Connect a Telegram chat to receive alerts.
        </Typography>
        <Button variant="contained" size="small" onClick={handleConnect} startIcon={<TelegramIcon />}>
          Connect Telegram
        </Button>
      </Box>
    );
  };

  // ---- render: subscriptions list (manage mode only) -------------------
  const renderSubsList = () => (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Your subscriptions ({subs.length})
      </Typography>
      {subs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No subscriptions yet. Open a spread chart from the matrix and click the
          🔔 icon to add one.
        </Typography>
      ) : (
        <List dense sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {subs.map((sub) => (
            <React.Fragment key={sub._id}>
              <ListItem
                secondaryAction={
                  <IconButton edge="end" size="small" onClick={() => handleDelete(sub._id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                alignItems="flex-start"
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {sub.ticker1} − r·{sub.ticker2}
                      </Typography>
                      {(() => {
                        // Resolve effective cooldown for each level.
                        // Priority: per-level array → legacy scalar (seconds)
                        // → legacy minutes×60 → default.
                        const arr = Array.isArray(sub.cooldownSecondsPerLevel)
                          ? sub.cooldownSecondsPerLevel : null;
                        const fallback = sub.cooldownSeconds != null
                          ? Number(sub.cooldownSeconds)
                          : (sub.cooldownMinutes != null
                              ? Number(sub.cooldownMinutes) * 60
                              : COOLDOWN_DEFAULT);
                        return sub.levels.map((L, i) => {
                          const cd = arr && Number.isFinite(Number(arr[i]))
                            ? Number(arr[i])
                            : fallback;
                          return (
                            <Chip
                              key={`${L}-${i}`}
                              size="small"
                              label={`${L.toFixed(2)} · ${humanizeCd(cd)}`}
                            />
                          );
                        });
                      })()}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      Bidirectional crossings · ratio read live from your dashboard
                    </Typography>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      )}
    </>
  );

  // ---- render: add form (add mode only) --------------------------------
  const renderAddForm = () => (
    <>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{t1}</Typography>
        <Typography variant="body2" color="text.secondary">−  r ·</Typography>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{t2}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          (ratio is read live from your dashboard at evaluation time)
        </Typography>
      </Box>
      {existingSub && (
        <MuiAlert severity="success" icon={<CheckCircleIcon fontSize="small" />} sx={{ mb: 2, py: 0 }}>
          You're currently subscribed for this pair. Editing the levels below
          will <strong>replace</strong> the saved set.
        </MuiAlert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 1 }}>
        <TextField
          label="Levels"
          placeholder="e.g. -22, -19, -16"
          size="small"
          value={levelsInput}
          onChange={(e) => setLevelsInput(e.target.value)}
          helperText={
            parsedLevels.length > 0
              ? `${parsedLevels.length} level(s) parsed — alerts fire on either-direction crossings. Set cooldown per level below.`
              : 'Comma- or space-separated numbers — these are the spread values that trigger an alert'
          }
          autoFocus
          fullWidth
        />
        {parsedLevels.length > 0 && (
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 1,
            p: 1.25, borderRadius: 1, border: 1, borderColor: 'divider',
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              Per-level cooldown — 0 fires every crossing, max 86400s (24h)
            </Typography>
            {parsedLevels.map((L, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="body2" sx={{
                  fontFamily: 'monospace', minWidth: 64, textAlign: 'right',
                  fontWeight: 600,
                }}>
                  {Number(L).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">→</Typography>
                <TextField
                  label="Cooldown (s)"
                  type="number"
                  size="small"
                  value={cooldownInputs[i] ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCooldownInputs((prev) => {
                      const next = prev.slice();
                      next[i] = v;
                      // Pad if needed (shouldn't happen with the sync effect,
                      // but defensive against React batching).
                      while (next.length <= i) next.push(String(COOLDOWN_DEFAULT));
                      return next;
                    });
                  }}
                  inputProps={{ min: COOLDOWN_MIN, max: COOLDOWN_MAX, step: 1 }}
                  sx={{ width: 140 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {humanizeCd(parsedCooldowns[i])}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {!paired && (
        <MuiAlert severity="info" sx={{ mt: 1 }}>
          Pair Telegram first (above) — you can still add the subscription, but
          alerts won't deliver until pairing is complete.
        </MuiAlert>
      )}
    </>
  );

  // ---- assemble --------------------------------------------------------
  const title = mode === 'add' ? 'Add spread alert' : 'Spread Alert Subscriptions';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={mode === 'add' ? 'sm' : 'md'}
      fullWidth
      PaperProps={{ sx: { minHeight: mode === 'add' ? 'auto' : '50vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TelegramIcon fontSize="small" />
        <span>{title}</span>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Banner: in 'add' mode hide it once paired (compact form-only UX). */}
        {(mode === 'manage' || !paired || pairingCode) && renderPairingBanner()}
        {error && <MuiAlert severity="error" sx={{ mb: 2 }}>{error}</MuiAlert>}
        {mode === 'manage' ? renderSubsList() : renderAddForm()}
      </DialogContent>

      <DialogActions>
        {/* Unsubscribe button: only shown when editing an existing sub.
            Sits on the left so it's visually distinct from Cancel/Save. */}
        {mode === 'add' && existingSub && (
          <Button
            color="error"
            disabled={loading}
            onClick={async () => {
              const action = await dispatch(deleteSubscription(existingSub._id));
              if (action.meta.requestStatus === 'fulfilled') {
                onClose?.();
              }
            }}
          >
            Unsubscribe
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>{mode === 'add' ? 'Cancel' : 'Close'}</Button>
        {mode === 'add' && (
          <Button
            variant="contained"
            disabled={!canAdd || loading}
            onClick={handleAdd}
          >
            {existingSub ? 'Save changes' : 'Add subscription'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AlertsManager;
